/**
 * PdfPage.js — A4 page container for SWMS PDF export.
 *
 * Migrated from SWMS Platform PdfPage.tsx → JavaScript.
 *
 * Each <PdfPage> renders a 210×297mm A4 page with:
 *   • header band (brand line + page label)
 *   • gold gradient bars
 *   • content area (flex:1 with padding)
 *   • footer band
 *
 * Usage:
 *   <PdfPage chapterCode="007" pageNum={1} total={5}>
 *     ... chapter content ...
 *   </PdfPage>
 *
 * The `data-pdf-page` attribute lets the export logic find all A4 pages
 * to capture via html2canvas → jsPDF.
 */

const GOLD = '#c9a84c';
const NAVY = '#1a1a2e';

export default function PdfPage({ children, chapterCode, pageNum, total, companyName, companyAbn }) {
  const bar = { height: '2px', background: `linear-gradient(90deg, ${GOLD} 0%, ${NAVY} 50%, ${GOLD} 100%)` };
  // Show header text for all pages with companyName; suppress page-code label for cover/toc
  const showMeta = companyName;
  const showPageLabel = chapterCode && !['cover', 'toc'].includes(chapterCode);

  return (
    <div
      data-pdf-page="true"
      data-chapter-code={chapterCode || ''}
      style={{
        width: '210mm',
        minHeight: '297mm',
        overflow: 'visible',
        background: 'white',
        fontFamily: "'Cabinet Grotesk', 'Inter', system-ui, sans-serif",
        color: NAVY,
        fontSize: '12px',
        lineHeight: 1.5,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header: text row then gradient bar */}
      <div style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9mm 25mm 5mm' }}>
          {showMeta && (
            <span style={{ fontSize: 7, color: '#7a7a8e', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
              {companyName}{companyAbn ? `  |  ABN: ${companyAbn}` : ''}
            </span>
          )}
          {showPageLabel && (
            <span style={{ fontSize: 7, color: '#7a7a8e', letterSpacing: 0.5, whiteSpace: 'nowrap', marginLeft: 'auto' }}>
              OHSE-{chapterCode}  |  Page {pageNum ?? 1}{total && total > 1 ? ` of ${total}` : ''}
            </span>
          )}
        </div>
        <div style={bar} />
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          padding: '4mm 25mm 0',
          overflow: 'visible',
          position: 'relative',
        }}
      >
        {children}
      </div>

      {/* Footer: gradient bar then 9mm spacer */}
      <div style={{ flexShrink: 0 }}>
        <div style={bar} />
        <div style={{ height: '9mm' }} />
      </div>
    </div>
  );
}

/**
 * Capture [data-pdf-page] elements within a container as a single PDF Blob.
 * Uses html2canvas to render each A4 page, then packs them into one jsPDF document.
 *
 * @param {HTMLElement}  container      root element whose [data-pdf-page] children to capture
 * @param {string[]}     [chapterCodes] optional — only capture pages with matching data-chapter-code
 * @param {Function}     [onProgress]   optional — called after each chapter completes:
 *                                      (doneCount, totalChapters, chapterCode) => void
 * @returns {Promise<Blob>}             PDF blob (application/pdf)
 */
export async function capturePdfPages(container, chapterCodes, onProgress) {
  const html2canvas = (await import('html2canvas')).default;
  const { default: jsPDF } = await import('jspdf');

  let pageEls = Array.from(container.querySelectorAll('[data-pdf-page]'));
  if (chapterCodes && chapterCodes.length > 0) {
    const codeSet = new Set(chapterCodes);
    pageEls = pageEls.filter(el => codeSet.has(el.getAttribute('data-chapter-code') || ''));
  }
  if (pageEls.length === 0) throw new Error('No PDF pages found to capture');

  // Group page elements by chapter code (preserving DOM order)
  const groupMap = new Map();
  for (const el of pageEls) {
    const code = el.getAttribute('data-chapter-code') || '';
    if (!groupMap.has(code)) groupMap.set(code, []);
    groupMap.get(code).push(el);
  }
  const chapterGroups = Array.from(groupMap.entries()); // [[code, [el, ...]], ...]

  const pdf = new jsPDF('p', 'mm', 'a4');
  const PAGE_W = 210;
  const PAGE_H = 297;
  let firstPage = true;

  for (let gi = 0; gi < chapterGroups.length; gi++) {
    const [code, els] = chapterGroups[gi];

    for (const el of els) {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        width: el.scrollWidth,
        height: el.scrollHeight,
        logging: false,
      });

      // Auto-pagination: split canvas into A4-height slices
      const pxPerMm = canvas.width / PAGE_W;
      const pageHeightPx = Math.round(pxPerMm * PAGE_H);
      const numSlices = Math.max(1, Math.ceil(canvas.height / pageHeightPx));

      for (let s = 0; s < numSlices; s++) {
        if (!firstPage) pdf.addPage();
        firstPage = false;

        const sourceY = s * pageHeightPx;
        const availableHeight = Math.min(pageHeightPx, canvas.height - sourceY);

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = pageHeightPx;
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, pageHeightPx);
        ctx.drawImage(
          canvas,
          0, sourceY, canvas.width, availableHeight,
          0, 0, canvas.width, availableHeight,
        );
        pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', 0, 0, PAGE_W, PAGE_H);
      }
    }

    if (onProgress) onProgress(gi + 1, chapterGroups.length, code);
  }

  return pdf.output('blob');
}

export { GOLD, NAVY };
